<script setup lang="ts">
import TableRowAccordeonElementalsListTrigger from './TableRowAccordeonElementalsListTrigger/index.vue'
import { useSidebarStore } from '~~/src/stores/sidebar'
import Accordeon, { type AccordeonEmits, type Props as AccordeonProps } from '@/components/Accordion/index.vue'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import { useDragAndDropStore } from '~~/src/stores/drag-and-drop'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import { useDropzone } from '@/composables/useDropzone'

type Props = {
  list: List | Elemental
  listId?: string | number
  parentListId?: string | number
  listTitle: string
  isMain?: boolean
  nPrompts: number
  iconBoxClass?: string
  elementalsType: ElementalTypeEnum
  folderLevel: number
} & AccordeonProps

const props = defineProps<Props>()

const emits = defineEmits<Emits>()

defineOptions({
  name: 'AccordeonElementalsList',
})

type Emits = AccordeonEmits & {
  (event: 'moved', from: number, to: number): void
  (event: 'droped', data: { elemental: Elemental; listId?: number }, index: number): void
}

const { isElementalOpen } = useSidebarStore()
const { state: store } = useDragAndDropStore()
const { state } = useElementalListsStore()

const accordeonRef = ref<InstanceType<typeof Accordeon> | null>(null)
const accordId = props.listId ? `${props.listId}` : `${props.list.id}`
const isOpen = isElementalOpen(accordId, props.parentListId
  ? `${props.parentListId}`
  : null)

const fn = useDebounceFn(() => {
  if (state.query === '')
    return
  const value = props.nPrompts
  if (accordeonRef.value)
    accordeonRef.value.toggle(value < 1)
}, 100)

watch(() => state.query, () => {
  fn()
})

const wrapperRef = computed(() => (accordeonRef.value?.wrapperRef as HTMLDivElement) ?? null)
const contentRef = ref<HTMLDivElement | null>(null)
const previewRef = ref<HTMLDivElement | null>(null)

const { isHover } = useDropzone({
  container: wrapperRef,
  previewArea: contentRef,
  previewComponent: previewRef,
  onDrop: (data, index) => {
    accordeonRef.value?.toggle(true)
    emits('droped', data as { elemental: Elemental; listId?: number }, index)
  },
  onMove: (from, to) => {
    emits('moved', from, to)
  },
  disabled: computed(() => props.isDisabled || props.isMain),
})

const elementalData = computed(() => (store.dragElementData as {
  listId?: string | number
  elemental: Elemental | null
})?.elemental)
</script>

<template>
  <Accordeon
    ref="accordeonRef"
    v-bind="$props"
    :is-open="isOpen"
    :accordion-id="accordId"
    class="rounded-lg transition-colors border-transparent p-0 border-none"
    ignore-events
    @on-close="$emit('onClose')"
    @on-open="$emit('onOpen')"
    @on-toggle="$emit(`onToggle`, $event)"
  >
    <template #header>
      <TableRowAccordeonElementalsListTrigger
        :parent-list-id="parentListId"
        :elementals-type="elementalsType"
        :list="list"
        :list-id="accordId"
        :is-main="isMain"
        :title="listTitle"
        :n-prompts="nPrompts"
        :icon-box-class="iconBoxClass"
        :class="[$style.accordeon, { 'bg-grayLighter/90': isHover }]"
      >
        <template #header>
          <slot name="header" />
        </template>
      </TableRowAccordeonElementalsListTrigger>
    </template>
    <template #content>
      <div
        class="flex flex-col gap-1 no-hover"
      >
        <div
          ref="contentRef"
          class="relative content-container"
          :class="[
            $style['no-scrollbar'],
            {
              'folder-level-1': folderLevel === 1,
              'folder-level-2': folderLevel === 2,
              'folder-level-3': folderLevel === 3,
            }]"
        >
          <li
            v-if="elementalData && isHover"
            ref="previewRef"
            class="accordeon-list-item relative"
          >
            <div
              class="h-0.5 w-full absolute left-0 bg-primary z-99999 drag-line"
            >
              <div i-mdi:play class="w-4 h-4 absolute top-[50%] -translate-y-1/2 -left-2 text-primary z-99999" />
            </div>
          </li>
          <slot name="content" />
        </div>
      </div>
    </template>
  </Accordeon>
</template>

<style lang="scss" scoped>
:deep(.content-container) {
  &.folder-level-1 {
    .accordeon-list-item {
      margin-left: 1rem;
    }
    .drag-line {
      left: 2rem;
    }
  }
  &.folder-level-2 {
    .accordeon-list-item {
      margin-left: 2rem;
    }
    .drag-line {
      left: 2rem;
    }
  }

  &.folder-level-3 {
    .accordeon-list-item {
      margin-left: 4rem;
    }
    .drag-line {
      left: 4rem;
    }
  }
}
</style>

<style module lang="scss">
.no-scrollbar {
  &::-webkit-scrollbar {
    display: none;
  }
}
.accordeon {
  border-radius: 0rem;

}
</style>
